import React, { useEffect } from "react";

import { usePageTitle } from "@/atoms/page-title-atom";
import { gql } from "@apollo/client";
import ErrorScreen from "@components/error-pages/error-screen";
import { useCachedQuery } from "@components/hooks";
import { StuckToTop } from "@components/stuck-to-top";
import { LoadingSlide, NoDataView } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useNorthbeamQuery } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type GetModelComparisonReport,
  type GetModelComparisonReportVariables,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { CommonGQLFragments } from "@utils/common-gql-fragments";
import { parseDateRangeArgument } from "@utils/index";

import { ModelComparisonTableSection } from "./model-comparison-section";
import { ReportBodyControl } from "./report-body-control";
import { useReportBodyState } from "./use-report-body-control";

function ModelComparisonPage(): JSX.Element {
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Model Comparison");
  }, [setPageTitle]);
  const { user } = useUser();
  const { state, breakdowns, granularity } = useReportBodyState();
  const anchor = todayLocalAsISODate();
  const dateRange = parseDateRangeArgument(state.dateRange, anchor);

  const {
    data: _data,
    loading,
    error,
  } = useNorthbeamQuery<
    GetModelComparisonReport,
    GetModelComparisonReportVariables
  >(GET_MODEL_COMPARISON_REPORT, {
    useHeavy: true,
    variables: { dateRange, granularity },
  });

  const data = useCachedQuery(_data);

  const paramsForLogging = React.useMemo(
    () => ({
      "Date Range Type":
        typeof state.dateRange === "string"
          ? state.dateRange
          : state.dateRange.type,
      Breakdowns: breakdowns.map((v) => v.key),
      Granularity: granularity,
      "Model 1": state.attributionModel1,
      "Model 2": state.attributionModel2,
    }),
    [
      breakdowns,
      state.dateRange,
      granularity,
      state.attributionModel1,
      state.attributionModel2,
    ],
  );

  const response = data?.me?.salesReport;
  React.useEffect(() => {
    if (response) {
      prettyLogEvent.visited({
        user,
        page: Pages.MODEL_COMPARISON,
        data: {
          ...paramsForLogging,
          ...state,
        },
      });
    }
  }, [response, paramsForLogging, state]);

  if (loading || !response) {
    return <LoadingSlide />;
  }

  if (response?.rows?.length === 0) {
    return <NoDataView />;
  }

  if (error) {
    return <ErrorScreen />;
  }

  return (
    <>
      <StuckToTop>{() => <ReportBodyControl isLoading={loading} />}</StuckToTop>
      <div className="p-4">
        <div className="x-full bg-white px-7 p-5 rounded-md h-full">
          <p>
            Use this to identify the differences in how your attribution models
            assign credit to touchpoints. Data is reported on a{" "}
            <strong>Infinity-Day</strong> window using <strong>Cash</strong>{" "}
            accounting.
          </p>
          <ModelComparisonTableSection
            isLoading={loading}
            response={response}
          />
        </div>
      </div>
    </>
  );
}

const GET_MODEL_COMPARISON_REPORT = gql`
  query GetModelComparisonReport(
    $dateRange: JSONObject!
    $granularity: String!
  ) {
    me {
      id
      salesReport(
        dateRange: $dateRange
        compareDateRange: $dateRange
        granularity: $granularity
        attributionMethod: "model_comparison"
        attributionWindow: "infinity"
        nbAccountingMode: "cash"
        timeGranularity: "daily"
      ) {
        rows {
          ...SalesReportRowAll
        }
      }
    }
  }

  fragment SalesReportRowAll on SalesReportRow {
    objectId
    nbtPlatformID
    name
    dimensions
    modelComparisonPoints
  }
`;

export default ModelComparisonPage;
